import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { searchUsers } from '../lib/api';
import { Badge, Card, EmptyState, ErrorBox, Loading, PageHeader, TextInput, formatDate } from '../components/ui';

export function UsersPage(): JSX.Element {
  const [q, setQ] = useState('');
  const usersQ = useQuery({
    queryKey: ['users', q],
    queryFn: () => searchUsers(q),
  });

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Search by display name, Telegram ID, or user id prefix."
      />
      <Card className="mb-4">
        <TextInput
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
        />
      </Card>
      {usersQ.isLoading && <Loading />}
      {usersQ.isError && <ErrorBox message={(usersQ.error as Error).message} />}
      {usersQ.data && usersQ.data.items.length === 0 && (
        <EmptyState>No users match.</EmptyState>
      )}
      {usersQ.data && usersQ.data.items.length > 0 && (
        <Card className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-text-soft">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Telegram ID</th>
                <th className="px-4 py-2 font-medium">Username</th>
                <th className="px-4 py-2 font-medium">Driver</th>
                <th className="px-4 py-2 font-medium">Rides</th>
                <th className="px-4 py-2 font-medium">Rating</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {usersQ.data.items.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0 hover:bg-bg">
                  <td className="px-4 py-2">
                    <Link
                      to={`/users/${u.id}`}
                      className="font-medium text-brand-navy hover:underline"
                    >
                      {u.displayName}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{u.telegramId}</td>
                  <td className="px-4 py-2 text-text-soft">
                    {u.telegramUsername ? `@${u.telegramUsername}` : '—'}
                  </td>
                  <td className="px-4 py-2">{u.isDriver ? '✓' : '—'}</td>
                  <td className="px-4 py-2">{u.ridesCompleted}</td>
                  <td className="px-4 py-2">
                    {u.ratingAvg !== null ? `${u.ratingAvg.toFixed(2)} (${u.ratingCount})` : '—'}
                  </td>
                  <td className="px-4 py-2">
                    {u.isSuspended ? (
                      <Badge tone="danger">Suspended</Badge>
                    ) : u.reportCountOpen > 0 ? (
                      <Badge tone="warning">{u.reportCountOpen} open reports</Badge>
                    ) : (
                      <Badge tone="success">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-text-soft">{formatDate(u.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
